'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import { useGeoCountryCode, useLocale } from '@/contexts/LocaleContext';
import { LOCALES, LOCALE_NAMES, DEFAULT_LOCALE, isValidLocale, type Locale } from '@/lib/i18n';
import { getLocaleFlag } from '@/lib/locale-flags';

interface LanguageSwitcherProps {
  /** Align dropdown: 'left' for mobile menu, 'right' for desktop header */
  align?: 'left' | 'right';
}

export function LanguageSwitcher({ align = 'right' }: LanguageSwitcherProps) {
  const locale = useLocale();
  const geoCountryCode = useGeoCountryCode();
  const pathname = usePathname();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  // Path without locale prefix (for switching). Default locale has no prefix in URL.
  const segments = pathname?.split('/').filter(Boolean) || [];
  const pathWithoutLang =
    segments.length > 0 && isValidLocale(segments[0]) && segments[0] !== DEFAULT_LOCALE
      ? (segments.length > 1 ? '/' + segments.slice(1).join('/') : '/')
      : (pathname || '/');

  const getSwitchUrl = React.useCallback(
    (targetLocale: Locale) => {
      const queryString =
        typeof window !== 'undefined' && window.location.search
          ? window.location.search
          : '';
      const params = new URLSearchParams({
        locale: targetLocale,
        path: `${pathWithoutLang}${queryString}`,
      });
      return `/api/locale?${params.toString()}`;
    },
    [pathWithoutLang],
  );

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Select language"
      >
        <span className="text-base leading-none" aria-hidden>{getLocaleFlag(locale, geoCountryCode)}</span>
        <span className="uppercase">{locale}</span>
        <svg
          className={`h-4 w-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className={`absolute top-full z-50 mt-1 rounded-lg border border-white/10 bg-[#1e2235] py-1 shadow-xl ${align === 'left' ? 'left-0 w-full min-w-[140px]' : 'right-0 min-w-[140px]'}`}>
          {LOCALES.map((loc) => {
            const targetPath = getSwitchUrl(loc as Locale);
            return (
              <button
                key={loc}
                type="button"
                onClick={() => {
                  setOpen(false);
                  window.location.assign(targetPath);
                }}
                className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-white/10 hover:text-white ${
                  loc === locale ? 'bg-[#ffb74d]/20 text-[#ffb74d]' : 'text-slate-300'
                }`}
              >
                <span className="text-base leading-none" aria-hidden>{getLocaleFlag(loc as Locale, geoCountryCode)}</span>
                {LOCALE_NAMES[loc as Locale]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
